import { Icon } from "@iconify/react";
import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import {
  addAddon,
  getAddonsGroup,
  updateAddon,
} from "../../reduxStore/slices/addonSlice";
import { useNotify } from "../../utils/snackbarUtils";

export default function Addons() {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    loading: isLoading,
    list,
    addonGroupList,
  } = useSelector((state) => state.addon);
  const { user } = useSelector((state) => state.auth);
  const storeId = user?.storeId;
  const { uid } = useParams();

  const [id, setId] = useState("");
  const [addonName, setAddonName] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [addonGroup, setAddonGroup] = useState("");
  const [recordStatus, setRecordStatus] = useState("");

  useEffect(() => {
    if (uid && list?.length) {
      const addon = list.find((a) => Number(a.uid) === Number(uid));

      if (addon) {
        setId(addon.id || "");
        setAddonName(addon.name || "");
        setDesc(addon.description || "");
        setPrice(addon.price || "");
        setAddonGroup(addon.addon_group_uid || "");
        setRecordStatus(addon.record_status || "");
      } else {
        goToListing();
      }
    }
  }, [uid, list]);

  useEffect(() => {
    if (storeId) {
      dispatch(getAddonsGroup({ storeId }));
    }
  }, [storeId, dispatch]);

  const goToListing = () => {
    navigate("/app/config/addons");
  };

  const addonValidations = () => {
    if (!addonName.trim()) {
      showError("Please enter addon name.");
      return false;
    }
    if (price === "" || isNaN(price)) {
      showError("Please enter a valid addon price.");
      return false;
    }
    if (addonGroup === "") {
      showError("Please select addon group.");
      return false;
    }
    return true;
  };

  const handleSaveAddon = async () => {
    if (!addonValidations()) return;
    try {
      const obj = {
        store_id: storeId,
        name: addonName,
        description: desc,
        price: Number(price),
        addon_group_uid: addonGroup,
      };
      const res = await dispatch(addAddon(obj)).unwrap();
      showSuccess(res.message);
      goToListing();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleUpdateAddon = async () => {
    if (!addonValidations()) return;
    try {
      const obj = {
        store_id: storeId,
        id: id,
        uid: Number(uid),
        name: addonName,
        description: desc,
        price: Number(price),
        addon_group_uid: addonGroup,
        record_status: recordStatus,
      };
      const res = await dispatch(updateAddon(obj)).unwrap();
      showSuccess(res.message);
      goToListing();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Example options
  const items = ["Pizza", "Burger", "Pasta", "Sandwich"];

  return (
    <Box p={3}>
      <Container>
        <Box sx={{ maxWidth: "600px", m: "0 auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title="Back to Addons">
                <IconButton onClick={() => navigate("/app/config/addons")}>
                  <Icon icon="eva:arrow-back-fill" />
                </IconButton>
              </Tooltip>
              <Typography variant="h5">
                {uid ? "Edit Addon" : "Add Addon"}
              </Typography>
            </Stack>
          </Stack>

          <Card sx={{ p: "30px", maxWidth: "600px", m: "0 auto" }}>
            {/* Form */}
            <Stack spacing={3} maxWidth="600px">
              <TextField
                autoComplete="addon_name"
                label="Addon Name"
                variant="outlined"
                fullWidth
                value={addonName}
                onChange={(e) => setAddonName(e.target.value)}
              />

              <TextField
                autoComplete="addon_desc"
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
              />

              <TextField
                autoComplete="addon_price"
                label="Price"
                type="number"
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
                helperText="Enter 0 for free addons"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              {/* <Autocomplete
              multiple
              options={items}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <TextField {...params} label="Applicable Items" />
              )}
            /> */}

              <TextField
                select
                label="Addon Group"
                value={addonGroup}
                onChange={(e) => setAddonGroup(e.target.value)}
                fullWidth
              >
                {addonGroupList?.length > 0 ? (
                  addonGroupList.map((group) => (
                    <MenuItem
                      key={group.id || group}
                      value={group.uid || group}
                    >
                      {group.name || group}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No addon groups available</MenuItem>
                )}
              </TextField>

              {/* Actions */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => navigate("/app/config/addons")}>
                  Cancel
                </Button>
                <AppButton
                  disabled={isLoading}
                  isLoading={isLoading}
                  onClick={uid ? handleUpdateAddon : handleSaveAddon}
                  variant="contained"
                >
                  {uid ? "Update" : "Save"}
                </AppButton>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}
